import { useState, type ReactElement, type ReactNode } from 'react';
import { useAdmin } from '../ui/Shell';
import { HBars, Kpis, Seg, Table, type Row } from '../ui/Block';
import { fmtDate } from '../lib/api';
import { describeErr, useData } from '../lib/hooks';
import { DBlock } from './dataShared';
import { TranscriptionDrawer } from './TranscriptionDrawer';
import { CATEGORIES, catLabel, money, pct, type CostTotals } from './costShared';

interface Top { id: string; name: string | null; live: boolean | null; at: string; revenue: number; netRevenue: number; cost: number; margin: number }
interface CustomerCostsData { totals: CostTotals; counts: Record<string, number>; categories: { category: string; usd: number; estimated: number }[]; topTranscriptions: Top[] }
type Period = 'all' | '30' | '7';

export function CustomerCosts({ principalId, frame }: { principalId: string; frame: (acts: ReactNode, body: ReactNode) => ReactElement }) {
  const ctx = useAdmin();
  const [period, setPeriod] = useState<Period>('all');
  const d = useData<CustomerCostsData>(ctx.env, `/admin/customers/${principalId}/costs${period === 'all' ? '' : `?days=${period}`}`, 0, ctx.paused);
  const c = d.data;
  const seg = <Seg<Period> value={period} onChange={setPeriod} options={[{ value: 'all', label: 'All time' }, { value: '30', label: '30 days' }, { value: '7', label: '7 days' }]} />;
  if (!c) {
    const err = describeErr(d.error);
    return frame(seg, <DBlock title="Costs" loading={d.loading} error={err ? { ...err, retry: d.reload } : undefined} />);
  }
  const t = c.totals;
  const color = (k: string) => CATEGORIES.find((x) => x.key === k)?.bg;
  const n = (k: string) => c.counts[k] ?? 0;
  const rows: Row[] = c.topTranscriptions.map((x) => ({
    key: x.id,
    onClick: () => ctx.openDrawer(`tx:${x.id}:costs`, <TranscriptionDrawer id={x.id} tab="costs" />, true),
    cells: [
      { t: x.name ?? x.id, fw: 500, sub: `${x.live ? 'Live' : 'File or link'} · ${fmtDate(x.at)}` },
      { t: money(x.revenue), mono: true },
      { t: money(x.cost), mono: true },
      { t: money(x.margin), mono: true, fw: 600, fg: x.margin < 0 ? 'var(--conf-low)' : 'var(--ok)' },
    ],
  }));
  return frame(seg, (
    <>
      <DBlock title="Profit / loss" sub="Revenue is credit charged before tax; what we keep is after Polar's fee (estimated). Paid in is money the account actually paid.">
        <Kpis min={120} items={[
          { label: 'Revenue', v: money(t.revenue), sub: `${n('transcription')} transcription${n('transcription') === 1 ? '' : 's'}${n('realtime') ? ` · ${n('realtime')} realtime` : ''}` },
          { label: 'We keep', v: money(t.netRevenue), sub: 'after Polar' },
          { label: 'Cost', v: money(t.cost), sub: t.cost ? `${money(t.estimated)} estimated` : undefined },
          { label: 'Profit', v: money(t.margin), fg: t.margin < 0 ? 'var(--conf-low)' : 'var(--ok)', dot: t.margin < 0 ? 'bad' : 'ok', sub: t.marginPct != null ? `${pct(t.marginPct)} of what we keep` : t.margin < 0 ? 'nothing kept: a loss' : undefined },
          { label: 'Paid in', v: money(t.paidIn ?? 0), sub: t.paidIn ? 'payments' : 'never paid: free credit only', fg: t.paidIn ? undefined : 'var(--conf-mid)' },
        ]} />
      </DBlock>
      <DBlock title="Cost by category" empty={c.categories.length ? undefined : 'Nothing cost anything in this period.'}>
        <HBars rows={c.categories.map((k) => ({ label: catLabel(k.category), n: k.usd, v: money(k.usd), bg: color(k.category) }))} />
      </DBlock>
      <DBlock title="Costliest transcriptions" sub="Click one for its cost lines." count={String(rows.length)} empty={rows.length ? undefined : 'No transcriptions in this period.'}>
        <div className="tbl--wrap"><Table mobile={ctx.mobile} minWidth={560} rows={rows} cols={[{ label: 'TRANSCRIPTION', width: 'minmax(200px,2fr)' }, { label: 'REVENUE', width: '90px', align: 'r' }, { label: 'COST', width: '90px', align: 'r' }, { label: 'MARGIN', width: '96px', align: 'r' }]} /></div>
      </DBlock>
    </>
  ));
}
